/**
 * Plafond d'un classeur Excel importé, en Mo — celui des imports budgétaires (`[maxSizeMb]="10"`).
 *
 * ExcelJS charge le classeur entier en mémoire avant toute lecture : sans borne, un fichier
 * volumineux ou une archive piégée fige l'onglet de l'agent (audit A-11).
 */
export const TAILLE_MAX_CLASSEUR_MO = 10;

/**
 * Texte d'une cellule ExcelJS, quelle que soit sa nature (audit R-15).
 *
 * `String(cellule.value)` donnait « [object Object] » sur une formule (`{ formula, result }`),
 * un texte riche (`{ richText: [...] }`) ou un lien (`{ text, hyperlink }`). La valeur calculée
 * d'une formule est retenue ; une date est rendue en `JJ-MM-AAAA` ; une cellule vide donne `''`.
 */
export function texteCellule(valeur: unknown): string {
    if (valeur === null || valeur === undefined) {
        return '';
    }
    if (valeur instanceof Date) {
        const jj = String(valeur.getUTCDate()).padStart(2, '0');
        const mm = String(valeur.getUTCMonth() + 1).padStart(2, '0');
        return `${jj}-${mm}-${valeur.getUTCFullYear()}`;
    }
    if (typeof valeur === 'object') {
        const objet = valeur as Record<string, unknown>;
        if ('result' in objet) {
            return texteCellule(objet['result']);
        }
        if (Array.isArray(objet['richText'])) {
            return (objet['richText'] as { text?: unknown }[])
                .map((r) => (typeof r.text === 'string' ? r.text : ''))
                .join('')
                .trim();
        }
        if ('text' in objet) {
            return texteCellule(objet['text']);
        }
        return '';
    }
    return String(valeur).trim();
}

/**
 * Clé de comparaison d'un numéro saisi : « 1 », « 01 » et « 1.0 » désignent le même numéro, que
 * le serveur recevrait trois fois sous la forme `numero: 1` (audit R-15). Un texte non numérique
 * est comparé tel quel.
 */
export function cleNumero(numero: string): string {
    const n = Number(numero.trim().replace(',', '.'));
    return numero.trim() !== '' && Number.isFinite(n) ? String(n) : numero.trim();
}

/** Message de refus d'un classeur trop volumineux, ou `null` s'il peut être chargé. */
export function refusTailleClasseur(fichier: File): string | null {
    return fichier.size > TAILLE_MAX_CLASSEUR_MO * 1024 * 1024
        ? `Le fichier « ${fichier.name} » dépasse ${TAILLE_MAX_CLASSEUR_MO} Mo : réduisez-le avant de l'importer.`
        : null;
}
